import { GoogleGenAI, Type } from "@google/genai";
import { CreativeBrief, CampaignConcept } from "../types";

// Store the API key provided by the user via the UI
let userApiKey: string | null = null;

export const setGeminiApiKey = (key: string) => {
  userApiKey = key;
};

// Create a new GoogleGenAI instance. 
// Priority: User Input Key -> Environment Variable -> Throw Error
const getAI = () => {
  const key = userApiKey || process.env.API_KEY;
  if (!key) {
    throw new Error("API Key is missing. Please enter a valid Gemini API Key.");
  }
  return new GoogleGenAI({ apiKey: key });
};

const synthesizePrompt = (brief: CreativeBrief): string => {
  const segments = [
    `Format: High-end Commercial Photography (Phase One XF IQ4).`,
    `Subjects: Two distinct individuals (Duo).`,
    `Vibe: ${brief.vibe}.`,
    `Location: ${brief.scene}.`,
    `Attire: ${brief.outfit}.`,
    `Lighting: ${brief.lighting} (physically accurate falloff).`,
    `Optics: ${brief.lens} (Depth of Field).`,
    `Grade: ${brief.realismTuning}.`,
    `Grooming: ${brief.hairStyle}, ${brief.makeupPalette}.`,
    `Skin Details: ${brief.skinStyle} (High frequency texture).`,
    `Anatomy: ${brief.bustLine}, ${brief.waistRatio}, ${brief.hipsSculpt}.`,
    `Angle: ${brief.cameraAngle}.`,
    brief.customPrompt ? `Notes: ${brief.customPrompt}.` : ""
  ].filter(Boolean);

  return segments.join(" | ");
};

export const generateCampaignConcepts = async (brief: CreativeBrief): Promise<CampaignConcept[]> => {
  const ai = getAI();
  const synthPrompt = synthesizePrompt(brief);

  const systemInstruction = `YOU ARE A WORLD-CLASS FASHION & PORTRAIT PHOTOGRAPHER (e.g., Annie Leibovitz, Mario Testino).
  
  TASK: Write a technical photography brief for a DUO shoot.
  
  GUIDELINES:
  1. Focus on LIGHTING PHYSICS, TEXTURE, and ATMOSPHERE.
  2. The scene must be realistic and grounded in the real world.
  3. Describe the interaction between the two subjects naturally.
  4. DO NOT describe specific facial features (references will be used).
  
  INPUT CONTEXT: ${synthPrompt}
  
  Output JSON ARRAY with 1 item.`;

  const parts: any[] = [{ text: `CONTEXT: ${synthPrompt}` }];
  
  brief.subjects.forEach((subject, sIdx) => {
    const frontal = subject.anchorImages.frontal;
    if (frontal) {
      const mime = frontal.split(';')[0].split(':')[1] || 'image/jpeg';
      parts.push({ text: `Reference for Person ${sIdx + 1}:` });
      parts.push({ 
        inlineData: { mimeType: mime, data: frontal.split(',')[1] } 
      });
    }
  });

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: { parts },
      config: {
        systemInstruction,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              id: { type: Type.STRING },
              themeIndex: { type: Type.NUMBER },
              title: { type: Type.STRING },
              description: { type: Type.STRING },
              prompt: { type: Type.STRING },
            },
            required: ["id", "themeIndex", "title", "description", "prompt"],
          },
        },
      },
    });

    const text = response.text?.trim();
    if (!text) throw new Error("Concept generation returned empty.");
    return JSON.parse(text);
  } catch (error: any) {
    console.error("Concept Error:", error);
    if (error.message?.includes("Requested entity was not found")) {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw error;
  }
};

export const generateTwinImage = async (
  brief: CreativeBrief,
  conceptPrompt: string
): Promise<string> => {
  const ai = getAI();
  const model = 'gemini-3-pro-image-preview';
  
  const parts: any[] = [];
  
  if (brief.subjects[0].anchorImages.frontal) {
    const img = brief.subjects[0].anchorImages.frontal;
    const mime = img.split(';')[0].split(':')[1] || 'image/jpeg';
    parts.push({ text: `REFERENCE IDENTITY A (Main Subject Left):` });
    parts.push({ inlineData: { mimeType: mime, data: img.split(',')[1] } });
  }

  if (brief.subjects[1].anchorImages.frontal) {
    const img = brief.subjects[1].anchorImages.frontal;
    const mime = img.split(';')[0].split(':')[1] || 'image/jpeg';
    parts.push({ text: `REFERENCE IDENTITY B (Main Subject Right):` });
    parts.push({ inlineData: { mimeType: mime, data: img.split(',')[1] } });
  }

  const finalPrompt = `
    PHOTOGRAPHIC REALISM MASTERPIECE.
    
    TASK: Generate a RAW, UNPROCESSED commercial photograph of TWO PEOPLE.
    
    IDENTITY STRICTNESS: MAX.
    1. SUBJECT A: Must match REFERENCE IDENTITY A exactly. Preserve biometric facial structure, moles, and skin tone.
    2. SUBJECT B: Must match REFERENCE IDENTITY B exactly. Preserve biometric facial structure, moles, and skin tone.
    
    SCENARIO: ${conceptPrompt}
    
    TECHNICAL SETTINGS:
    - Camera: Phase One XF IQ4 150MP.
    - Lens: ${brief.lens} with realistic bokeh and depth of field.
    - Lighting: ${brief.lighting}. Use Ray-traced global illumination logic. Shadows must be pitch black where appropriate (high dynamic range).
    - Skin: ${brief.skinStyle}. RENDER EVERY PORE. Do not smooth the skin. We need "dermatological realism" - vellus hair, texture, micro-imperfections.
    - Finish: ${brief.realismTuning}. Add slight film grain (ISO 400).
    
    COMPOSITION:
    - The two subjects are interacting naturally in the space.
    - Correct scale and perspective relative to the environment.
    - Clothing fabrics must show weave and texture (${brief.outfit}).
    
    NEGATIVE PROMPT:
    (Do not generate) -> CGI, 3D render, illustration, painting, airbrushed, smooth skin, plastic look, doll-like, oversaturated, bad anatomy, floating objects, blurry faces, cartoon.
  `.trim();

  parts.push({ text: finalPrompt });

  try {
    const response = await ai.models.generateContent({
      model,
      contents: { parts },
      config: {
        imageConfig: {
          aspectRatio: brief.aspectRatio,
          imageSize: brief.imageSize || "1K" // 1K is often sharper for faces than 2K upscale on some models, but user can select
        }
      },
    });

    const candidate = response.candidates?.[0];
    if (!candidate) throw new Error("No candidates returned from Image Gen.");
    
    const imagePart = candidate.content.parts.find(p => !!p.inlineData);
    if (imagePart?.inlineData) {
      return `data:image/png;base64,${imagePart.inlineData.data}`;
    }
    
    throw new Error("No image data found in response parts.");
  } catch (error: any) {
    console.error("Render Error:", error);
    if (error.message?.includes("Requested entity was not found")) {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw error;
  }
};